import {AbstractControl, FormGroup, ValidatorFn} from "@angular/forms";
import {TranslationUtil} from "./translation-util";
import {Calendar, LocaleSettings} from "primeng/calendar";
import {EMPTY_STRING, PatternConstant} from "../constants/global.constant";
import isEqual from 'lodash/isEqual';


export class GlobalUtils {

  /**
   * Generates a label from the first letters of a list of strings.
   *
   * @param {string[]} array - An array of strings.
   * @returns {string} - A string composed of the first letter of each element in the array, all in uppercase.
   */
  public static generateLabelForIcon(array: string[]): string {
    let label = EMPTY_STRING;
    array.forEach(s => label = s !== undefined ? `${label}${s.slice(0, 1).toUpperCase()}` : null);
    return label;
  }

  /**
   * Returns CSS styling for an avatar based on gender.
   *
   * @param {string} gender - The gender, represented by 'F' for female or any other value for male.
   * @returns {object} - An object with CSS properties for the avatar.
   */
  public static avatarStyleByGender(gender: string) {
    return {
      'color': gender === 'F' ? '#EA527E' : '#1A214D',
      'background-color': '#E3E3E3',
      'border-color': '#FFFFFF'
    };
  }

  /**
   * Returns default CSS styling for an avatar.
   *
   * @returns {object} - An object with default CSS properties for the avatar.
   */
  public static avatarStyle() {
    return {
      'color': '#FFFFFF',
      'background-color': '#64748b',
      'border-color': '#FFFFFF'
    };
  }

  /**
   * Concatenates the first name and last name of a user to get the full name.
   *
   * @param {any} user - A user object with `prenom` (first name) and `nom` (last name) properties.
   * @returns {string} - The full name of the user.
   */
  public static getFullName(user: any): string {
    return `${user.prenom} ${user.nom}`;
  }

  /**
   * Removes elements from an array based on another array.
   *
   * @param {any[]} array - The original array from which elements will be removed.
   * @param {any[]} arrayToFind - The array of elements to remove from the original array.
   * @param {any} [key] - Optional, the key to compare if the elements are objects.
   * @returns {any[]} - An array with elements from the original array that are not in the array to remove.
   */
  public static removeArrayFromArray(array: any[], arrayToFind: any[], key?: any): any[] {
    const filteredArray: any[] = [];
    if (array) {
      array.forEach(item => {
        let exist = arrayToFind.find(element => isEqual(element, item) || isEqual(element[`${key}`], item[`${key}`])) === undefined;
        if (exist) {
          filteredArray.push(item);
        }
      });
    }
    return filteredArray;
  }

  /**
   * Finds elements in an array that are also present in another array.
   *
   * @param {any[]} array - The original array.
   * @param {any[]} arrayToFind - The array to search for elements within.
   * @param {any} [key] - Optional, the key to compare if the elements are objects.
   * @returns {any[]} - An array with elements from the original array that are also in the array to search.
   */
  public static findArrayWithinArray(array: any[], arrayToFind: any[], key?: any): any[] {
    const filteredArray: any[] = [];
    if (array) {
      array.forEach(item => {
        if (arrayToFind.find(element => isEqual(element, item) || (key && isEqual(element[`${key}`], item[`${key}`]))) !== undefined) {
          filteredArray.push(item);
        }
      });
    }
    return filteredArray;
  }

  /**
   * Initializes the calendar settings with translated values.
   * To use this function, first inject the primeNGConfig in the app.component's constructor then : primeNGConfig.setTranslation(GlobalUtils.initCalendar())
   * @returns A LocaleSettings object representing the configured settings for the calendar.
   */
  public static initCalendar(): LocaleSettings {
    Calendar.prototype.getDateFormat = () => PatternConstant.DATE_FORMAT;
    Calendar.prototype._firstDayOfWeek = 1;
    return {
      firstDayOfWeek: 1,
      dayNames:
        [
          TranslationUtil.translate('calendar.day-names.sunday'),
          TranslationUtil.translate('calendar.day-names.monday'),
          TranslationUtil.translate('calendar.day-names.tuesday'),
          TranslationUtil.translate('calendar.day-names.wednesday'),
          TranslationUtil.translate('calendar.day-names.thursday'),
          TranslationUtil.translate('calendar.day-names.friday'),
          TranslationUtil.translate('calendar.day-names.saturday')],
      dayNamesShort: [
        TranslationUtil.translate('calendar.day-names-short.sun'),
        TranslationUtil.translate('calendar.day-names-short.mon'),
        TranslationUtil.translate('calendar.day-names-short.tus'),
        TranslationUtil.translate('calendar.day-names-short.wed'),
        TranslationUtil.translate('calendar.day-names-short.thu'),
        TranslationUtil.translate('calendar.day-names-short.fri'),
        TranslationUtil.translate('calendar.day-names-short.sat')
      ],
      dayNamesMin: [
        TranslationUtil.translate('calendar.day-names-min.su'),
        TranslationUtil.translate('calendar.day-names-min.mo'),
        TranslationUtil.translate('calendar.day-names-min.tu'),
        TranslationUtil.translate('calendar.day-names-min.we'),
        TranslationUtil.translate('calendar.day-names-min.th'),
        TranslationUtil.translate('calendar.day-names-min.fr'),
        TranslationUtil.translate('calendar.day-names-min.sa')
      ],
      monthNames: [
        TranslationUtil.translate('calendar.month-names.january'),
        TranslationUtil.translate('calendar.month-names.february'),
        TranslationUtil.translate('calendar.month-names.march'),
        TranslationUtil.translate('calendar.month-names.april'),
        TranslationUtil.translate('calendar.month-names.may'),
        TranslationUtil.translate('calendar.month-names.june'),
        TranslationUtil.translate('calendar.month-names.july'),
        TranslationUtil.translate('calendar.month-names.august'),
        TranslationUtil.translate('calendar.month-names.september'),
        TranslationUtil.translate('calendar.month-names.october'),
        TranslationUtil.translate('calendar.month-names.november'),
        TranslationUtil.translate('calendar.month-names.december')
      ],
      monthNamesShort: [
        TranslationUtil.translate('calendar.month-names-short.jan'),
        TranslationUtil.translate('calendar.month-names-short.feb'),
        TranslationUtil.translate('calendar.month-names-short.mar'),
        TranslationUtil.translate('calendar.month-names-short.apr'),
        TranslationUtil.translate('calendar.month-names-short.may'),
        TranslationUtil.translate('calendar.month-names-short.jun'),
        TranslationUtil.translate('calendar.month-names-short.jul'),
        TranslationUtil.translate('calendar.month-names-short.aug'),
        TranslationUtil.translate('calendar.month-names-short.sep'),
        TranslationUtil.translate('calendar.month-names-short.sct'),
        TranslationUtil.translate('calendar.month-names-short.nov'),
        TranslationUtil.translate('calendar.month-names-short.dec')
      ],
      today: TranslationUtil.translate('calendar.today'),
      clear: TranslationUtil.translate('calendar.clear'),
      dateFormat: TranslationUtil.translate('calendar.date-format'),
      weekHeader: TranslationUtil.translate('calendar.week-header')
    };
  }

  /**
   * Global date validator to validate period between start and end date
   * */

  public static validateDates(formGroup: FormGroup) {
    if (formGroup.contains('startPeriod') && formGroup.contains('endPeriod')) {
      const startDate = formGroup.get('startPeriod').value;
      const endDate = formGroup.get('endPeriod').value;
      if (!startDate || !endDate) {
        return null;
      }
      return startDate && endDate && startDate < endDate ? null : {datesMismatch: true};
    }
    return null;
  }

  /**
   * Validates that the minimum payment amount is less than or equal to the maximum payment amount.
   *
   * @param {FormGroup} formGroup - The form group containing the payment amount fields.
   * @returns {null | {amountMismatch: boolean}} - Returns `null` if validation passes, or an error object if the amounts are invalid.
   */
  public static validateAmount(formGroup: FormGroup) {
    if (formGroup.contains('minPaymentAmount') && formGroup.contains('maxPaymentAmount')) {
      const min = formGroup.get('minPaymentAmount').value;
      const max = formGroup.get('maxPaymentAmount').value;
      if (!min || !max) {
        return null;
      }
      return min && max && min <= max ? null : {amountMismatch: true};
    }
    return null;
  }

  /**
   * Creates a validator function that checks if the control's value is a positive decimal number.
   *
   * @returns {ValidatorFn} - A validator function that returns an error object if the value is not a positive decimal number, or `null` if valid.
   */
  public static positiveDecimalNumber(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = parseFloat(control.value);
      if (isNaN(value) || value <= 0) {
        return {'positiveDecimalNumber': {value: control.value}};
      }
      return null;
    };
  }

  /**
   * Checks if a value is considered empty.
   *
   * A value is considered empty if:
   * - It is `null` or `undefined`
   * - For arrays, it is empty or all its elements are empty strings (trimmed) or equal to 'NULL'
   * - For objects, it has no own enumerable properties, or all its values are empty
   *
   * @param {any} value - The value to check for emptiness.
   * @returns {boolean} - Returns `true` if the value is empty; otherwise, returns `false`.
   */
  public static isEmpty(value: any): boolean {
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        const emptyStringPredicate = (object: any) => {
          return object === null || object === undefined
            || typeof object === "string" && (!object || object.trim().length === 0 || object?.trim().toUpperCase() === 'NULL');
        }
        return value === null || value.length === 0 || value.every(emptyStringPredicate);
      }
      return value === null || value === undefined || Object.keys(value).length === 0 || this.isEmpty(Object.values(value));
    }
    return value === null || value === undefined;
  }

  /**
   * Filters a list of objects based on a search query
   * Used with PrimeNG AutoComplete.
   *
   * @param {string} key - The key to search in the objects.
   * @param {any} _event - The event containing the query string.
   * @param {T[] | any} list - The list of objects to filter.
   * @returns {T[]} - A filtered list containing objects that match the search query.
   */
  public static filterList<T>(key: string, _event: any, list: T[] | any): T[] {
    return list ? list.filter(value => value[key]?.toLowerCase().includes(_event?.query?.toLowerCase())) : [];
  }

  /**
   * Rounds a number to two decimal places.
   *
   * @param {number} value - The number to round.
   * @returns {number} - The number rounded to two decimal places.
   */
  public static transformDecimal(value: number): number {
    return Number((Math.round(value * 100) / 100).toFixed(2));
  }


  /**
   * Converts a Date object to a formatted display string in the format 'DD/MM/YYYY'.
   *
   * @param {Date} date - The Date object to be converted to a display string.
   * @returns {string} - A string representing the formatted date in the 'DD/MM/YYYY' format.
   */
  public static dateToDisplay(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Converts a base64-encoded string to a Blob object.
   *
   * @param {string} base64Data - The base64-encoded string to be converted to a Blob.
   * @param {string} contentType - The content type of the Blob, e.g., 'image/png', 'application/pdf', etc.
   * @returns {Blob} - A Blob object representing the binary data decoded from the base64 string.
   */
  public static base64toBlob(base64Data: string, contentType: string): Blob {
    const sliceSize = 512;
    const byteCharacters = atob(base64Data);
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, {type: contentType});
  }

  /**
   * Adds a comma after every two characters in a string.
   *
   * @param {string} input - The input string to modify.
   * @returns {string} - The modified string with a comma added after every two characters.
   *
   * @example
   * // Returns "12,34,56,78"
   * addCommaAfterTwoChars("12345678");
   */
  public static addCommaAfterTwoChars(input: string): string {
    return input.replace(/(.{2})(?!$)/g, '$&,'); //todo move to const
  }

  /**
   * Downloads a file from an ArrayBuffer.
   *
   * Creates a `Blob` from the ArrayBuffer and triggers a download with the given file name.
   *
   * @param {ArrayBuffer} arrayBuffer - The binary data to download.
   * @param {string} file - The name of the file to save.
   */
  public static downloadBlobFile(arrayBuffer: ArrayBuffer | any, file: string) {
    const blob = new Blob([arrayBuffer], {type: 'application/octet-stream'});
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = file;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }


  /**
   * Used for local storage items which can potentially
   * exceed the cookies size limit (Chrome 4096 bytes)
   */
  public static setStorageItem(key: string, data: any): void {
    const keyPrefix = location.pathname.split('/')[1];
    const fullKey = keyPrefix ? keyPrefix +'-'+ key : key;
    localStorage.setItem(fullKey, data);
  }

  public static getStorageItem(key: string): string {
    const keyPrefix = location.pathname.split('/')[1];
    const fullKey = keyPrefix ? keyPrefix +'-'+ key : key;
    return localStorage.getItem(fullKey);
  }

  public static convertSizeInMO(fileSizeInBytes: number): number {
    return fileSizeInBytes / (1000 * 1000);
  }
}
